import { ErrorBoundary } from '@sentry/react';
import { Dropdown } from '@statsbomb/kitbag-components';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMatch, useNavigate, useSearchParams } from 'react-router-dom';
import { trackGeneralUiInteractionEvent } from '@/analytics/snowplow/analytics';
import { resetCompetitionAverageFiltersAtom } from '@/atoms/filters/competitionAverage';
import { resetComparisonFiltersAtom } from '@/atoms/filters/resetComparisonFilters';
import { unwrappedPlayersSearchIndexAtom } from '@/atoms/queries/players';
import { unwrappedTeamsSearchIndexAtom } from '@/atoms/queries/teams';
import { FlagIcon } from '@/components/icons/FlagIcon';
import { COMPETITION_AVERAGE_OPTION_VALUE } from '@/consts/comparison';
import { MAX_OPTION_SEARCH_RESULTS, SEARCH_DEBOUNCE_MS } from '@/consts/search';
import { ACTIVE_TAB, COMPARISON_ID, COMPARISON_TYPE } from '@/consts/searchKeys';
import { useUpdateQueryString } from '@/hooks/useUpdateQueryString';
import { ComparisonType } from '@/types/comparison';
import { NumberOption } from '@/types/generic';
import { ComparisonFilterTab } from '@/types/tabs';
import { OptionSearchIndex, searchOptions } from '@/utils/optionSearch';
import { GeneralErrorBoundaryFallback } from '../errorBoundary/ErrorBoundaryFallback';

type OptionLabelContext = 'menu' | 'value';

const formatNumberOptionLabel = (option: NumberOption, { context }: { context: OptionLabelContext }): string => {
  const nickname = option.optional?.nickname;
  if (context === 'value' && !!nickname) return nickname;
  return option.label;
};

const ComparisonDropdownBase = ({
  entity,
  searchIndex,
  isLoading,
}: {
  entity: 'team' | 'player';
  searchIndex?: OptionSearchIndex;
  isLoading?: boolean;
}) => {
  const { t } = useTranslation(['radar', 'entity', 'accessibility']);
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState('');
  const [debouncedInput, setDebouncedInput] = useState('');
  const [searchParams] = useSearchParams();
  const updateQueryString = useUpdateQueryString(searchParams);
  const comparisonIdParam = searchParams.get(COMPARISON_ID);
  const isCompetitionAverageMode = searchParams.get(COMPARISON_TYPE) === ComparisonType.CompetitionAverage;
  const numericComparisonIdParam = comparisonIdParam ? Number(comparisonIdParam) : null;
  const resetComparisonFilters = useSetAtom(resetComparisonFiltersAtom);
  const resetCompetitionAverageFilters = useSetAtom(resetCompetitionAverageFiltersAtom);
  const competitionAverageLabel = t('competitionAverage.optionLabel', { ns: 'radar' });

  useEffect(() => {
    const inputDebounce = setTimeout(() => setDebouncedInput(inputValue), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(inputDebounce);
  }, [inputValue]);

  const filteredOptions = useMemo(() => {
    if (!searchIndex) return [];
    const results = debouncedInput.length
      ? searchOptions(searchIndex, debouncedInput, MAX_OPTION_SEARCH_RESULTS)
      : [...searchIndex.byId.values()].slice(0, MAX_OPTION_SEARCH_RESULTS);
    return results.map(option => ({
      ...option,
      icon: option.optional?.countryCode ? (
        <FlagIcon naturalDimensions countryCode={option.optional.countryCode} size="regular" />
      ) : undefined,
      context: option.optional?.context,
    }));
  }, [searchIndex, debouncedInput]);

  // Pin "Competition Average" to the top of the dropdown options list
  const competitionOption = {
    value: COMPETITION_AVERAGE_OPTION_VALUE,
    label: competitionAverageLabel,
  };
  const options = [competitionOption, ...filteredOptions];

  const onChange = (option: NumberOption | null) => {
    resetComparisonFilters();
    resetCompetitionAverageFilters();
    if (option) {
      const isCompetitionAverage = option.value === COMPETITION_AVERAGE_OPTION_VALUE;
      const comparisonParams = isCompetitionAverage
        ? [{ key: COMPARISON_TYPE, value: ComparisonType.CompetitionAverage }]
        : [
            { key: COMPARISON_ID, value: String(option.value) },
            { key: COMPARISON_TYPE, value: ComparisonType.Entity },
          ];

      navigate(
        `?${updateQueryString(
          [...comparisonParams, { key: ACTIVE_TAB, value: ComparisonFilterTab.COMPARISON }],
          isCompetitionAverage ? [COMPARISON_ID] : [],
        )}`,
      );

      trackGeneralUiInteractionEvent({
        interactionType: 'select',
        itemLocation: `${entity} radar`,
        itemName: 'comparison dropdown',
        itemType: 'dropdown',
      });
    } else {
      navigate(`?${updateQueryString([], [COMPARISON_ID, ACTIVE_TAB, COMPARISON_TYPE])}`);
    }
  };

  const entityValue = (numericComparisonIdParam && searchIndex?.byId.get(numericComparisonIdParam)) || null;
  const value = isCompetitionAverageMode ? competitionOption : entityValue;

  const label = t('comparisonDropdown.label', { entity: t(`${entity}_one`, { ns: 'entity' }) });
  const clearIndicatorLabel = t('dropdown.clearIndicator.single', { ns: 'accessibility' });

  return (
    <div role="group" aria-label={label}>
      <Dropdown
        id={`${entity}-comparison-dropdown`}
        label={label}
        placeholder={t('comparisonDropdown.placeholder', { context: entity })}
        labelPosition="none"
        size="small"
        value={value}
        isLoading={isLoading}
        isSearchable
        onChange={onChange}
        options={options}
        isClearable
        clearIndicatorLabel={clearIndicatorLabel}
        dropdownIndicatorIcon="Search"
        onInputChange={setInputValue}
        filterOption={null}
        formatOptionLabel={formatNumberOptionLabel}
      />
    </div>
  );
};

const ComparisonDropdownWithData = ({ entity }: { entity: 'team' | 'player' }) => {
  const searchIndex = useAtomValue(
    entity === 'player' ? unwrappedPlayersSearchIndexAtom : unwrappedTeamsSearchIndexAtom,
  );

  return <ComparisonDropdownBase entity={entity} searchIndex={searchIndex} isLoading={!searchIndex} />;
};

export const ComparisonDropdown = () => {
  const isPlayerSection = !!useMatch('/:locale/player/*');
  const entity = isPlayerSection ? 'player' : 'team';

  return (
    <ErrorBoundary fallback={<GeneralErrorBoundaryFallback />}>
      <ComparisonDropdownWithData entity={entity} />
    </ErrorBoundary>
  );
};
